import styled from 'styled-components'
import border from '../../tokens/border'
import colors from '../../tokens/colors'
import fonts from '../../tokens/fonts'
import typography from '../../tokens/typography'
import { defaultTheme } from '../../theming/data/defaultTheme'
import { composeTheme } from '../../utils/compose-theme'

export const Wrapper = styled.button.attrs((props) => composeTheme(props))`
  align-items: center;
  background-color: transparent;
  border-color: transparent;
  border-radius: ${(props) => props.theme.button.borderRadius};
  border-width: ${border.width.widthThin};
  cursor: pointer;
  display: flex;
  font-family: ${fonts.inter};
  font-size: ${(props) => props.theme.button.fontSize};
  font-style: ${(props) => props.theme.button.fontStyle};
  font-weight: ${(props) => props.theme.button.fontWeight};
  height: ${(props) => props.theme.button.height};
  justify-content: center;
  letter-spacing: normal;
  line-height: ${typography.fontHeight.lineHeightDefault};
  min-width: 12rem;
  text-transform: none;
  white-space: nowrap;
  padding: ${(props) => props.theme.button.paddingTop}
    ${(props) => props.theme.button.paddingRight}
    ${(props) => props.theme.button.paddingBottom}
    ${(props) => props.theme.button.paddingLeft};

  &:disabled {
    cursor: not-allowed;
  }

  .loading-spinner {
    .base {
      border-color: ${(props) => props.theme.button.loadingBorderColor};
      border-right-color: ${(props) =>
        props.theme.button.loadingBorderRightColor};
    }
  }

  &.primary {
    background-color: ${(props) =>
      props.theme.button.primaryBackgroundColor ||
      props.theme.brandColors.primaryPure};
    color: ${(props) => props.theme.button.primaryColor};
    border: ${(props) => props.theme.button.primaryBorder};

    &:hover {
      background-color: ${(props) =>
        props.theme.button.primaryHoverBackgroundColor ||
        props.theme.brandColors.primaryMedium};
      color: ${(props) => props.theme.button.primaryHoverColor};
      box-shadow: ${(props) => props.theme.button.primaryHoverShadow};
    }

    &:active {
      background-color: ${(props) =>
        props.theme.button.primaryActiveBackgroundColor ||
        props.theme.brandColors.primaryDark};
    }

    &:disabled {
      background-color: ${(props) =>
        props.theme.button.primaryDisabledBackgroundColor};
      color: ${(props) => props.theme.button.primaryDisabledColor};
      border: ${(props) => props.theme.button.primaryDisabledBorder};
    }

    &:focus {
      outline: ${(props) => props.theme.button.primaryFocusOutline};
      box-shadow: ${(props) => props.theme.button.primaryFocusShadow};
    }

    svg {
      circle {
        stroke: ${(props) => props.theme.brandColors.primaryDark};
      }
    }

    .loading-spinner {
      .base {
        border-color: ${(props) => props.theme.brandColors.primaryDark};
        border-right-color: ${(props) =>
          props.theme.button.pimaryLoadingBorderRightColor};
      }
    }
  }

  &.secondary {
    background-color: ${(props) => props.theme.button.secondaryBackgroundColor};
    color: ${(props) => props.theme.button.secondaryColor};
    border: ${(props) => props.theme.button.secondaryBorder};

    &:hover {
      background-color: ${(props) =>
        props.theme.button.secondaryHoverBackgroundColor};
      color: ${(props) => props.theme.button.secondaryHoverColor};
      box-shadow: ${(props) => props.theme.button.secondaryHoverShadow};
    }

    &:active {
      background-color: ${(props) =>
        props.theme.button.secondaryActiveBackgroundColor};
    }

    &:disabled {
      background-color: ${(props) =>
        props.theme.button.secondaryDisabledBackgroundColor};
      color: ${(props) => props.theme.button.secondaryDisabledColor};
      border: ${(props) => props.theme.button.secondaryDisabledBorder};
    }

    &:focus {
      outline: ${(props) => props.theme.button.secondaryFocusOutline};
      box-shadow: ${(props) => props.theme.button.secondaryFocusShadow};
    }

    svg {
      circle {
        stroke: ${colors.neutralColorLowLight};
      }
    }
  }

  &.tertiary {
    background-color: ${(props) => props.theme.button.tertiaryBackgroundColor};
    color: ${(props) => props.theme.button.tertiaryColor};
    border: ${(props) => props.theme.button.tertiaryBorder};

    &:hover {
      background-color: ${(props) =>
        props.theme.button.tertiaryHoverBackgroundColor};
      color: ${(props) => props.theme.button.tertiaryHoverColor};
      box-shadow: ${(props) => props.theme.button.tertiaryHoverShadow};
    }

    &:active {
      background-color: ${(props) =>
        props.theme.button.tertiaryActiveBackgroundColor};
    }

    &:disabled {
      background-color: ${(props) =>
        props.theme.button.tertiaryDisabledBackgroundColor};
      color: ${(props) => props.theme.button.tertiaryDisabledColor};
      border: ${(props) => props.theme.button.tertiaryDisabledBorder};
    }

    &:focus {
      outline: ${(props) => props.theme.button.tertiaryFocusOutline};
      box-shadow: ${(props) => props.theme.button.tertiaryFocusShadow};
    }

    svg {
      circle {
        stroke: ${colors.neutralColorLowLight};
      }
    }
  }

  &.danger {
    background-color: ${(props) => props.theme.button.dangerBackgroundColor};
    color: ${(props) => props.theme.button.dangerColor};
    border: ${(props) => props.theme.button.dangerBorder};

    &:hover {
      background-color: ${(props) =>
        props.theme.button.dangerHoverBackgroundColor};
      color: ${(props) => props.theme.button.dangerHoverColor};
      box-shadow: ${(props) => props.theme.button.dangerHoverShadow};
    }

    &:active {
      background-color: ${(props) =>
        props.theme.button.dangerActiveBackgroundColor};
    }

    &:disabled {
      background-color: ${(props) =>
        props.theme.button.dangerDisabledBackgroundColor};
      color: ${(props) => props.theme.button.dangerDisabledColor};
      border: ${(props) => props.theme.button.dangerDisabledBorder};
    }

    &:focus {
      outline: ${(props) => props.theme.button.dangerFocusOutline};
      box-shadow: ${(props) => props.theme.button.dangerFocusShadow};
    }

    svg {
      circle {
        stroke: ${colors.feedbackColorNegativeDark};
      }
    }

    .loading-spinner {
      .base {
        border-color: ${(props) => props.theme.button.dangerLoadingBorderColor};
        border-right-color: ${(props) =>
          props.theme.button.dangerLoadingBorderRightColor};
      }
    }
  }

  &.positive {
    background-color: ${(props) => props.theme.button.positiveBackgroundColor};
    color: ${(props) => props.theme.button.positiveColor};
    border: ${(props) => props.theme.button.positiveBorder};

    &:hover {
      background-color: ${(props) =>
        props.theme.button.positiveHoverBackgroundColor};
      color: ${(props) => props.theme.button.positiveHoverColor};
      box-shadow: ${(props) => props.theme.button.positiveHoverShadow};
    }

    &:active {
      background-color: ${(props) =>
        props.theme.button.positiveActiveBackgroundColor};
    }

    &:disabled {
      background-color: ${(props) =>
        props.theme.button.positiveDisabledBackgroundColor};
      color: ${(props) => props.theme.button.positiveDisabledColor};
      border: ${(props) => props.theme.button.positiveDisabledBorder};
    }

    &:focus {
      outline: ${(props) => props.theme.button.positiveFocusOutline};
      box-shadow: ${(props) => props.theme.button.positiveFocusShadow};
    }

    svg {
      circle {
        stroke: ${colors.feedbackColorPositiveDark};
      }
    }

    .loading-spinner {
      .base {
        border-color: ${(props) =>
          props.theme.button.positiveLoadingBorderColor};
        border-right-color: ${(props) =>
          props.theme.button.positiveLoadingBorderRightColor};
      }
    }
  }

  &.icon-only {
    height: 4rem;
    max-width: 4rem;
    min-width: 4rem;
    padding: 1.2rem;
  }

  &.full-width {
    width: 100%;
  }
`

Wrapper.defaultProps = defaultTheme

export const TextSpacer = styled.span`
  display: block;
  margin-left: 0.8rem;
`
